/**
 * Snapshot augmentation helpers for PlaywrightClient.
 *
 * @module playwright-client/snapshot-augmentation
 * @see docs/api/services/browser-session.md
 */

import type { Page } from 'playwright';
import { errorMessage } from '../errors-internal.js';
import type { BrowserLogger } from '../logger.js';
import { parseSnapshot, type SnapshotElement } from '../snapshot-analyzer.js';
import { runPerRefAugmentations } from './per-ref-augmentation.js';
import { resolveRefs } from './resolve-refs.js';

/**
 * Derive a human-readable label from a URL pathname.
 * Takes the last non-empty path segment, drops trailing IDs/UUIDs/numbers,
 * and title-cases the remaining words. Returns undefined if nothing useful
 * can be extracted (e.g. "/" or "/123"). Used as a fallback for icon-only
 * anchor links in sidebar nav rails.
 */
export function labelFromPathname(pathname: string | undefined): string | undefined {
  if (!pathname) return undefined;
  const segments = pathname.split(/[?#]/)[0].split('/').filter(Boolean);
  if (segments.length === 0) return 'Home';

  // Drop trailing segments that are obviously IDs (uuid, numeric, long hex)
  const meaningful = [...segments];
  while (meaningful.length > 1) {
    const last = meaningful[meaningful.length - 1];
    if (/^[0-9a-f-]{8,}$/i.test(last) || /^\d+$/.test(last)) {
      meaningful.pop();
    } else {
      break;
    }
  }

  const tail = meaningful[meaningful.length - 1];
  if (!tail) return undefined;

  // If last segment is itself an ID with no prior words, fall back to parent.
  if (/^[0-9a-f-]{8,}$/i.test(tail) || /^\d+$/.test(tail)) return undefined;

  // Split on dashes/underscores, title-case, rejoin.
  const words = tail.split(/[-_]/).filter(Boolean);
  if (words.length === 0) return undefined;
  const titled = words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  return titled.length > 0 && titled.length < 40 ? titled : undefined;
}

function updateElementLineByRef(
  yaml: string,
  ref: string,
  updater: (line: string) => string
): string {
  const refToken = `[ref=${ref}]`;
  const lines = yaml.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.includes(refToken)) continue;
    if (!line.trimStart().startsWith('- ')) continue;
    lines[i] = updater(line);
    break;
  }

  return lines.join('\n');
}

function appendAttributeIfMissing(line: string, key: string, value: string | undefined): string {
  if (!value) return line;
  if (line.includes(`[${key}=`) || line.includes(`[${key}]`)) return line;

  const hasTrailingColon = /:\s*$/.test(line);
  const baseLine = hasTrailingColon ? line.replace(/:\s*$/, '') : line;
  return `${baseLine} [${key}=${value}]${hasTrailingColon ? ':' : ''}`;
}

/**
 * Augment icon-only controls (buttons/links) in the snapshot YAML with DOM hints.
 */
/**
 * Source categories for substrate-v2 `~name-src` provenance, recorded per ref
 * whenever this augmenter injects an augmented name. The caller is expected
 * to translate these into substrate-v2 `~name-src=augmented:dom-mutation`
 * attrs (the substrate currently has a single "augmented" bucket; the
 * sub-category is logged for diagnostics).
 */
export type AugmentationSource =
  | 'link-pathname'
  | 'button-icon'
  | 'generic-icon'
  | 'img-alt'
  | 'toggle-context';

/**
 * Legacy entry point kept as a thin shim around `runPerRefAugmentations`.
 *
 * Production code (v1 `captureSnapshot`, v2 `captureSubstrateV2`) calls
 * the orchestrator directly. This shim exists for back-compat with
 * `snapshot-augmentation.test.ts` and one-off tooling
 * (`scripts/scenarios/capture-from-trace.ts`). The legacy 600-line body
 * with broken `document.querySelector('[aria-ref=…]')` calls was
 * deleted; the orchestrator's `resolveRefs`-based per-element checks
 * replicate every augmentation it used to produce.
 *
 * The `provenance` side-channel parameter is accepted for back-compat
 * and ignored — the orchestrator emits `~name-src=augmented:dom-mutation`
 * inline as a YAML attribute when `opts.v2` is true.
 */
export async function augmentUnlabeledElements(
  page: Page,
  yaml: string,
  logger: BrowserLogger,
  preParsedElements?: SnapshotElement[],
  _provenance?: Map<string, AugmentationSource>
): Promise<string> {
  const out = await runPerRefAugmentations(page, yaml, logger, { preParsedElements });
  return out.yaml;
}


/**
 * Augment grid cell values from the live DOM.
 *
 * Delegates to the consolidated per-ref pass — the legacy body used
 * `document.querySelector('[aria-ref=…]')`, a silent no-op in
 * production. Kept exported as a stable callsite for tests and the
 * v1 pipeline orchestration.
 */
export async function augmentGridCellValues(
  page: Page,
  yaml: string,
  logger: BrowserLogger,
  preParsedElements?: SnapshotElement[],
  skipGridRefs?: Set<string>
): Promise<string> {
  const out = await runPerRefAugmentations(page, yaml, logger, {
    preParsedElements,
    skipGridRefs,
  });
  return out.yaml;
}

/**
 * Augment form fields with their visual labels. Many apps don't use
 * `<label for>` or `aria-label` — the label is just text positioned
 * before the input.
 *
 * Delegates to the consolidated per-ref pass. The legacy body used
 * `document.querySelector('[aria-ref=…]')` and was a silent no-op in
 * production. The new path covers the explicit-label and
 * preceding-sibling tiers; the dedup/glyph-fallback tiers from the
 * legacy implementation are tracked as follow-up work.
 */
export async function augmentFormFieldLabels(
  page: Page,
  yaml: string,
  logger: BrowserLogger,
  preParsedElements?: SnapshotElement[]
): Promise<string> {
  const out = await runPerRefAugmentations(page, yaml, logger, { preParsedElements });
  return out.yaml;
}

/**
 * Annotate textbox/combobox/searchbox refs with `[readonly]` / `[disabled]`
 * when the underlying DOM element is non-editable. Playwright's
 * `page.ariaSnapshot({ mode: 'ai' })` collapses readonly state — every
 * `<textarea readonly>` and `<input disabled>` shows up as a plain
 * `textbox`, indistinguishable from an editable one. Without this pass an
 * agent can pick a readonly preview as a type target and stall for two
 * stacked 10s timeouts on Playwright's "element is not editable" gate.
 *
 * Standards-based signals only: `:read-only`, `aria-readonly="true"`,
 * `contenteditable="false"`, `disabled`, `aria-disabled="true"`. No
 * coincidental class/style heuristics — if the page doesn't carry one of
 * these, the agent sees the field as editable (same as today).
 */
export async function augmentFormFieldEditability(
  page: Page,
  yaml: string,
  logger: BrowserLogger,
  preParsedElements?: SnapshotElement[]
): Promise<string> {
  const out = await runPerRefAugmentations(page, yaml, logger, { preParsedElements });
  return out.yaml;
}

/**
 * Enrich interactive SVG elements so they appear as labeled groups
 * in the accessibility tree.
 */
/**
 * Enrich hidden clickable elements from known UI library patterns.
 * These elements (e.g., Chosen.js clear buttons rendered as `<abbr>`) have no
 * ARIA role and are invisible to the accessibility tree. We inject `role="button"`
 * and derive an `aria-label` so the agent can see and click them.
 *
 * Must run BEFORE ariaSnapshot() so the browser promotes them into the a11y tree.
 */
export async function enrichHiddenClickableElements(page: Page, logger: BrowserLogger): Promise<void> {
  try {
    const enrichedCount = await page.evaluate(() => {
      let count = 0;

      // --- Chosen.js close buttons: derive label from associated field name ---
      const chosenCloseButtons = document.querySelectorAll('abbr.search-choice-close');
      for (const el of chosenCloseButtons) {
        if (el.getAttribute('data-enriched-clickable') === 'true') continue;
        if (el.getAttribute('role') === 'button') continue;

        let fieldName: string | undefined;

        // Walk up to .chosen-container parent
        const container = el.closest('.chosen-container');
        if (container) {
          // Find associated <select> (should be previous sibling of the container)
          const selectEl = container.previousElementSibling;
          if (selectEl && selectEl.tagName === 'SELECT') {
            const selectId = selectEl.id;
            // Try label[for=selectId]
            if (selectId) {
              const labelEl = document.querySelector(`label[for="${selectId}"]`);
              if (labelEl?.textContent?.trim()) {
                fieldName = labelEl.textContent.trim();
              }
            }
            // Fallback: aria-label on the select
            if (!fieldName) {
              fieldName = selectEl.getAttribute('aria-label') || undefined;
            }
          }
          // Fallback: look for a preceding sibling label of the container's parent
          if (!fieldName && container.parentElement) {
            const prev = container.parentElement.previousElementSibling;
            if (prev && (prev.tagName === 'LABEL' || prev.querySelector('label'))) {
              const labelText = prev.textContent?.trim();
              if (labelText && labelText.length < 80) fieldName = labelText;
            }
          }
        }

        const label = fieldName ? `Clear ${fieldName}` : 'Clear selection';
        el.setAttribute('role', 'button');
        el.setAttribute('aria-label', label);
        el.setAttribute('data-enriched-clickable', 'true');
        count++;
      }

      // --- Generic patterns: [selector, fallbackLabel] ---
      const genericPatterns: Array<[string, string]> = [
        ['.select2-selection__clear', 'select2-selection__clear'],
      ];

      for (const [selector, fallbackLabel] of genericPatterns) {
        const elements = document.querySelectorAll(selector);
        for (const el of elements) {
          if (el.getAttribute('data-enriched-clickable') === 'true') continue;
          if (el.getAttribute('role') === 'button') continue;

          const label =
            el.getAttribute('title') ||
            el.getAttribute('aria-label') ||
            el.id ||
            fallbackLabel;

          el.setAttribute('role', 'button');
          el.setAttribute('aria-label', label);
          el.setAttribute('data-enriched-clickable', 'true');
          count++;
        }
      }

      // --- Hide Chosen.js search inputs when dropdown is closed ---
      // When the dropdown is open (.chosen-with-drop), the search input stays visible for typing.
      const chosenSearchInputs = document.querySelectorAll(
        '.chosen-container:not(.chosen-with-drop) .chosen-search-input'
      );
      for (const input of chosenSearchInputs) {
        input.setAttribute('aria-hidden', 'true');
      }

      return count;
    });

    if (enrichedCount > 0) {
      logger.debug('[DirectPlaywright] Enriched hidden clickable elements', {
        count: enrichedCount,
      });
    }
  } catch (err) {
    logger.debug('[DirectPlaywright] Hidden clickable enrichment failed', {
      error: errorMessage(err),
    });
  }
}

/**
 * Augment hidden `<select>` elements with their option values.
 *
 * Libraries like Chosen.js and Select2 hide the native `<select>` (display:none)
 * and render custom div-based widgets. Playwright's a11y tree only sees the visible
 * widget trigger, not the hidden options. This function surfaces those options so the
 * agent knows what choices are available before clicking.
 *
 * Scoped to known patterns only: Chosen.js (.chosen-container) and Select2 (.select2-container).
 */
export async function augmentHiddenSelectOptions(
  page: Page,
  yaml: string,
  logger: BrowserLogger
): Promise<string> {
  try {
    // Phase A: Single page.evaluate to find all hidden selects and their widget triggers
    const hiddenSelects = await page.evaluate(() => {
      const results: Array<{
        options: string[];
        triggerText: string | null;
        triggerRect: { x: number; y: number; width: number; height: number } | null;
        fieldLabel: string | null;
      }> = [];

      const KNOWN_CONTAINERS = '.chosen-container, .select2-container';
      const selects = document.querySelectorAll('select');

      for (const sel of selects) {
        const style = window.getComputedStyle(sel);
        if (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          sel.offsetWidth > 0 &&
          sel.offsetHeight > 0
        )
          continue;

        const opts = Array.from(sel.options)
          .map((o) => o.text.trim())
          .filter((t) => t.length > 0 && t !== 'undefined');
        if (opts.length === 0) continue;

        const parent = sel.parentElement;
        if (!parent) continue;

        // Find the custom widget trigger among siblings
        let trigger: Element | null = null;
        for (const child of parent.children) {
          if (child === sel) continue;
          if (child.matches(KNOWN_CONTAINERS) || child.querySelector(KNOWN_CONTAINERS)) {
            // Find the clickable trigger inside the widget container
            trigger =
              child.querySelector('a, [role="combobox"], [role="listbox"], input[type="text"]') ||
              child;
            break;
          }
        }
        if (!trigger) continue;

        const rect = trigger.getBoundingClientRect();
        const triggerText = trigger.textContent?.trim()?.slice(0, 80) || null;

        // Derive field label from associated <label> element
        let fieldLabel: string | null = null;
        const selectId = sel.id;
        if (selectId) {
          const labelEl = document.querySelector(`label[for="${selectId}"]`);
          if (labelEl?.textContent?.trim()) {
            fieldLabel = labelEl.textContent.trim();
          }
        }
        if (!fieldLabel) {
          fieldLabel = sel.getAttribute('aria-label') || null;
        }
        // Fallback: check for a preceding label sibling of the parent wrapper
        if (!fieldLabel && parent.previousElementSibling) {
          const prev = parent.previousElementSibling;
          if (prev.tagName === 'LABEL' || prev.querySelector('label')) {
            const labelText = prev.textContent?.trim();
            if (labelText && labelText.length < 80) fieldLabel = labelText;
          }
        }

        results.push({
          options: opts.slice(0, 15),
          triggerText,
          triggerRect:
            rect.width > 0
              ? { x: rect.x, y: rect.y, width: rect.width, height: rect.height }
              : null,
          fieldLabel,
        });
      }
      return results;
    });

    if (hiddenSelects.length === 0) return yaml;

    logger.debug('[DirectPlaywright] Found hidden selects with options', {
      count: hiddenSelects.length,
    });

    // Phase B: Match triggers to snapshot refs and inject options
    const elements = parseSnapshot(yaml);
    let augmented = yaml;

    // Lazy-build a bounding-box map for clickable refs. Only fetched on first
    // use (when text matching fails), so we avoid the work when text matches suffice.
    let refBoxMap: Map<string, { x: number; y: number; width: number; height: number }> | null =
      null;

    const getRefBoxMap = async () => {
      if (refBoxMap) return refBoxMap;

      const candidateRefs: string[] = [];
      const collectClickableRefs = (el: SnapshotElement): void => {
        if (
          el.ref &&
          (el.role === 'link' || (el.role === 'generic' && el.attributes['cursor'] === 'pointer'))
        ) {
          candidateRefs.push(el.ref);
        }
        for (const child of el.children) {
          collectClickableRefs(child);
        }
      };
      for (const el of elements) {
        collectClickableRefs(el);
      }

      // Fetch all bounding boxes via resolveRefs (the legacy
      // `document.querySelector('[aria-ref=...]')` here was a silent no-op
      // because Playwright stores ref ids on `element._ariaRef` in the
      // isolated world; only the `aria-ref=` selector engine resolves
      // them).
      const boxes = await resolveRefs<{ x: number; y: number; width: number; height: number } | null>(
        page,
        candidateRefs,
        (el) => {
          const r = el.getBoundingClientRect();
          if (r.width === 0 && r.height === 0) return null;
          return { x: r.x, y: r.y, width: r.width, height: r.height };
        }
      );
      refBoxMap = new Map();
      for (const [ref, box] of Object.entries(boxes)) {
        if (box) refBoxMap.set(ref, box);
      }
      return refBoxMap;
    };

    for (const selectInfo of hiddenSelects) {
      const { options: opts, triggerText, triggerRect, fieldLabel } = selectInfo;
      if (!opts.length) continue;

      // Find candidate refs by matching trigger text against element text
      let matchedRef: string | null = null;

      if (triggerText) {
        const findByText = (el: SnapshotElement): string | null => {
          // Match clickable generics or links whose text matches the trigger text
          if (
            el.ref &&
            (el.role === 'generic' || el.role === 'link') &&
            el.text &&
            triggerText.includes(el.text.trim())
          ) {
            return el.ref;
          }
          for (const child of el.children) {
            const found = findByText(child);
            if (found) return found;
          }
          return null;
        };
        for (const el of elements) {
          matchedRef = findByText(el);
          if (matchedRef) break;
        }
      }

      // Fallback: match by bounding box position (lazy-fetched on first use)
      if (!matchedRef && triggerRect) {
        const boxes = await getRefBoxMap();
        for (const [ref, box] of boxes) {
          if (
            Math.abs(box.x - triggerRect.x) < 20 &&
            Math.abs(box.y - triggerRect.y) < 20
          ) {
            matchedRef = ref;
            break;
          }
        }
      }

      if (!matchedRef) continue;

      const optionsValue = opts.join('|');
      augmented = updateElementLineByRef(augmented, matchedRef, (line) => {
        let next = appendAttributeIfMissing(line, 'options', optionsValue);
        if (fieldLabel) {
          next = appendAttributeIfMissing(next, 'field', fieldLabel);
        }
        return next;
      });
    }

    return augmented;
  } catch (err) {
    logger.debug('[DirectPlaywright] Hidden select augmentation failed', {
      error: errorMessage(err),
    });
    return yaml;
  }
}

/**
 * Enrich positioned overlays that look like modals but lack `role="dialog"`.
 * Many apps (e.g., AngularJS, jQuery UI) render modals as plain `<div>` elements
 * with high z-index and no ARIA dialog role. This function detects those elements
 * and injects `role="dialog"` + `aria-label` so the agent recognizes them as dialogs.
 *
 * Must run BEFORE ariaSnapshot() so the browser promotes them into the a11y tree.
 */
export async function enrichModalDialogElements(page: Page, logger: BrowserLogger): Promise<void> {
  try {
    const enrichedCount = await page.evaluate(() => {
      let count = 0;
      const MAX_ENRICHMENTS = 3;

      // Skip elements that are already dialogs or already enriched
      function shouldSkip(el: Element): boolean {
        if (el.getAttribute('role') === 'dialog' || el.getAttribute('role') === 'alertdialog') return true;
        if (el.getAttribute('data-enriched-dialog') === 'true') return true;
        return false;
      }

      function isVisible(el: Element): boolean {
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        if (el instanceof HTMLElement && el.offsetWidth === 0 && el.offsetHeight === 0) return false;
        return true;
      }

      function enrichAsDialog(el: Element): void {
        if (count >= MAX_ENRICHMENTS) return;
        if (shouldSkip(el)) return;
        if (!isVisible(el)) return;

        el.setAttribute('role', 'dialog');

        // Derive aria-label from first heading
        const heading = el.querySelector('h1, h2, h3, h4, h5, h6');
        if (heading?.textContent?.trim()) {
          el.setAttribute('aria-label', heading.textContent.trim());
        }

        el.setAttribute('data-enriched-dialog', 'true');
        count++;
      }

      function hasInteractiveContent(el: Element): boolean {
        return el.querySelector('input, textarea, select, button, a[href], [role="button"], [role="link"], [role="textbox"], [role="combobox"]') !== null;
      }

      // Strategy 1: Class/attribute patterns
      const CLASS_PATTERNS = [
        /\bmodal(?![-_]?(backdrop|mask|overlay|fade|bg|background))\b/i,
        /\bdialog\b/i,
        /\bpopup\b/i,
        /\blightbox\b/i,
        /\bdrawer\b/i,
      ];
      const FRAMEWORK_SELECTORS = [
        '.cdk-overlay-pane',
        '.mat-dialog-container', '.mat-mdc-dialog-container',
        '.ui-dialog',
        '.modal-dialog',
        '.ant-modal-content',
        '.el-dialog',
        '.v-dialog',
        '.p-dialog',
      ];

      // Check framework selectors first (more precise)
      for (const selector of FRAMEWORK_SELECTORS) {
        if (count >= MAX_ENRICHMENTS) break;
        const els = document.querySelectorAll(selector);
        for (const el of els) {
          if (count >= MAX_ENRICHMENTS) break;
          if (hasInteractiveContent(el)) {
            enrichAsDialog(el);
          }
        }
      }

      // The three heuristic strategies below each scan the whole DOM. They only
      // setAttribute (no structural DOM change), so the element set is identical
      // across all three — query `*` once and reuse the static NodeList instead
      // of walking the entire (often huge) DOM three times. Lazily computed so a
      // page that already hit MAX_ENRICHMENTS via framework selectors never pays
      // for it. Output is byte-identical (same elements, same order).
      let allDomElements: NodeListOf<Element> | null = null;
      const getAllDomElements = (): NodeListOf<Element> =>
        (allDomElements ??= document.querySelectorAll('*'));

      // Check class patterns on all visible positioned elements
      if (count < MAX_ENRICHMENTS) {
        const allElements = getAllDomElements();
        for (const el of allElements) {
          if (count >= MAX_ENRICHMENTS) break;
          const className = el.className;
          if (typeof className !== 'string') continue;
          if (CLASS_PATTERNS.some(pattern => pattern.test(className))) {
            if (hasInteractiveContent(el)) {
              enrichAsDialog(el);
            }
          }
        }
      }

      // Strategy 2: Backdrop detection
      if (count < MAX_ENRICHMENTS) {
        const allFixed = getAllDomElements();
        for (const el of allFixed) {
          if (count >= MAX_ENRICHMENTS) break;
          if (!(el instanceof HTMLElement)) continue;
          const style = window.getComputedStyle(el);
          if (style.position !== 'fixed' && style.position !== 'absolute') continue;

          const rect = el.getBoundingClientRect();
          const viewportW = window.innerWidth;
          const viewportH = window.innerHeight;
          const coversViewport = rect.width >= viewportW * 0.8 && rect.height >= viewportH * 0.8;
          if (!coversViewport) continue;

          // Check for semi-transparent background
          const bg = style.backgroundColor;
          const opacity = parseFloat(style.opacity);
          const isSemiTransparent = (bg.includes('rgba') && !bg.includes('rgba(0, 0, 0, 0)') && bg.match(/,\s*([\d.]+)\)/) && parseFloat(bg.match(/,\s*([\d.]+)\)/)?.[1] || '1') < 1) || (opacity < 1 && opacity > 0);

          if (!isSemiTransparent) continue;

          // This looks like a backdrop — find the modal content
          // Check next sibling
          const nextSib = el.nextElementSibling;
          if (nextSib && nextSib instanceof HTMLElement && isVisible(nextSib) && hasInteractiveContent(nextSib)) {
            enrichAsDialog(nextSib);
            continue;
          }

          // Check parent's children with higher z-index
          if (el.parentElement) {
            const backdropZ = parseInt(style.zIndex) || 0;
            for (const sibling of el.parentElement.children) {
              if (sibling === el || !(sibling instanceof HTMLElement)) continue;
              const sibStyle = window.getComputedStyle(sibling);
              const sibZ = parseInt(sibStyle.zIndex) || 0;
              if (sibZ > backdropZ && isVisible(sibling) && hasInteractiveContent(sibling)) {
                enrichAsDialog(sibling);
                break;
              }
            }
          }
        }
      }

      // Strategy 3: Visual heuristics fallback
      if (count < MAX_ENRICHMENTS) {
        const allElements = getAllDomElements();
        for (const el of allElements) {
          if (count >= MAX_ENRICHMENTS) break;
          if (!(el instanceof HTMLElement)) continue;
          if (el.getAttribute('data-enriched-dialog') === 'true') continue;
          if (el.getAttribute('role') === 'dialog' || el.getAttribute('role') === 'alertdialog') continue;

          const style = window.getComputedStyle(el);
          if (style.position !== 'fixed' && style.position !== 'absolute') continue;

          const zIndex = parseInt(style.zIndex) || 0;
          if (zIndex <= 100) continue;

          if (!isVisible(el)) continue;

          const rect = el.getBoundingClientRect();
          if (rect.width < 200 || rect.height < 200) continue;

          // Must not cover the whole viewport (that's a backdrop, not a modal)
          const viewportW = window.innerWidth;
          const viewportH = window.innerHeight;
          if (rect.width >= viewportW * 0.95 && rect.height >= viewportH * 0.95) continue;

          if (hasInteractiveContent(el)) {
            enrichAsDialog(el);
          }
        }
      }

      return count;
    });

    if (enrichedCount > 0) {
      logger.debug('[DirectPlaywright] Enriched modal dialog elements', {
        count: enrichedCount,
      });
    }
  } catch (err) {
    logger.debug('[DirectPlaywright] Modal dialog enrichment failed', {
      error: errorMessage(err),
    });
  }
}

/**
 * Substrate-v2 modal/dialog detection — read-only sibling of
 * {@link enrichModalDialogElements}.
 *
 * The v2 modal-promotion path now lives in
 * `playwright-client/per-ref-augmentation.ts` as the `modalV2`
 * CheckId; callers should invoke `runPerRefAugmentations(scope, yaml,
 * logger, { v2: true })` and read `result.v2ModalOverrides`.
 */
export type { V2ModalOverride } from './per-ref-augmentation.js';

export async function enrichInteractiveSVGElements(page: Page, logger: BrowserLogger): Promise<void> {
  try {
    const enrichedCount = await page.evaluate(() => {
      let count = 0;
      const shapes = document.querySelectorAll('svg path[id], svg polygon[id], svg rect[id], svg circle[id]');

      for (const shape of shapes) {
        // Skip shapes with pointer-events: none
        const shapeStyle = window.getComputedStyle(shape);
        if (shapeStyle.pointerEvents === 'none') continue;

        // Walk to parent <g>
        const parentG = shape.closest('g');
        if (!parentG) continue;

        // Skip if already enriched or already has aria-label
        if (parentG.hasAttribute('data-svg-enriched')) continue;
        if (parentG.getAttribute('aria-label')) continue;

        // Derive label: <title> in group → <text> in group → shape id
        let label: string | undefined;

        const titleEl = parentG.querySelector('title');
        if (titleEl?.textContent?.trim()) {
          label = titleEl.textContent.trim();
        }

        if (!label) {
          const textEl = parentG.querySelector('text');
          if (textEl?.textContent?.trim()) {
            label = textEl.textContent.trim();
          }
        }

        if (!label) {
          const shapeId = shape.getAttribute('id');
          if (shapeId) label = shapeId;
        }

        if (!label) continue;

        parentG.setAttribute('aria-label', label);
        parentG.setAttribute('data-svg-enriched', 'true');
        count++;
      }

      return count;
    });

    if (enrichedCount > 0) {
      logger.debug('[DirectPlaywright] Enriched interactive SVG elements', {
        count: enrichedCount,
      });
    }
  } catch (err) {
    // Non-fatal: enrichment failure should not block snapshot
    logger.debug('[DirectPlaywright] SVG enrichment failed', {
      error: errorMessage(err),
    });
  }
}

/**
 * Augment search/filter textboxes with filter-state attributes
 * (`[filter-visible=N] [filter-query-in-results=bool]`).
 *
 * Implementation lives in the consolidated per-ref pass; this export
 * stays as a stable callsite for downstream code that still calls the
 * legacy name.
 */
export async function augmentFilterState(
  page: Page,
  yaml: string,
  logger: BrowserLogger,
  preParsedElements?: SnapshotElement[]
): Promise<string> {
  const out = await runPerRefAugmentations(page, yaml, logger, { preParsedElements });
  return out.yaml;
}


/**
 * Stamp `[aria-modal=true]` onto dialog YAML lines whose live-DOM container
 * is verifiably blocking the page — i.e. the dialog's body-level ancestor
 * has at least one sibling marked `inert` or `aria-hidden="true"`. That's
 * the standards-based fingerprint for a focus-trapping modal regardless of
 * whether the dialog element itself remembered to set `aria-modal`.
 *
 * Without this signal, the section formatter can't safely suppress the
 * background page shell when a modal is open: a bare `[role=dialog]` may be
 * a non-modal popover or drawer where the rest of the page is legitimately
 * still interactive. We only ever *add* the attribute; never strip refs
 * based on dialog role alone.
 */
export async function augmentBlockingModalSignal(
  page: Page,
  yaml: string,
  logger: BrowserLogger,
  preParsedElements?: SnapshotElement[]
): Promise<string> {
  try {
    const elements = preParsedElements ?? parseSnapshot(yaml);
    if (elements.length === 0) return yaml;

    // Find the active dialog/alertdialog in the parsed YAML — the one
    // currently focused. We only ever stamp aria-modal=true on the active
    // dialog; non-active dialogs in the tree are stale or background.
    let activeDialogRef: string | null = null;
    let alreadyAriaModal = false;
    const findActiveDialog = (el: SnapshotElement): boolean => {
      if (
        (el.role === 'dialog' || el.role === 'alertdialog') &&
        el.ref &&
        (el.attributes['active'] === 'true' || / \[active\]/.test(el.rawLine))
      ) {
        activeDialogRef = el.ref;
        if (el.attributes['aria-modal'] === 'true') alreadyAriaModal = true;
        return true;
      }
      for (const child of el.children) {
        if (findActiveDialog(child)) return true;
      }
      return false;
    };
    for (const el of elements) if (findActiveDialog(el)) break;
    if (!activeDialogRef || alreadyAriaModal) return yaml;

    // Verify a blocking modal is on the page by structural DOM check —
    // any [role=dialog] (live or framework-enriched) whose body-level
    // ancestor has at least one inert / aria-hidden=true sibling. We don't
    // try to per-ref match (Playwright's ariaSnapshot AI mode does not
    // reliably persist aria-ref attributes) — just confirm "a blocking
    // modal exists" and apply to the YAML's active dialog.
    const isBlocking = await page.evaluate(() => {
      const docBody = document.body;
      if (!docBody) return false;
      const dialogs = Array.from(
        document.querySelectorAll('[role=dialog], [role=alertdialog]')
      );
      for (const dialog of dialogs) {
        if (dialog.closest('[inert],[aria-hidden="true"]')) continue;
        let bodyChild: Element | null = dialog;
        while (bodyChild && bodyChild.parentElement && bodyChild.parentElement !== docBody) {
          bodyChild = bodyChild.parentElement;
        }
        if (!bodyChild || bodyChild.parentElement !== docBody) continue;
        for (const sibling of Array.from(docBody.children)) {
          if (sibling === bodyChild) continue;
          if (
            sibling.hasAttribute('inert') ||
            sibling.getAttribute('aria-hidden') === 'true'
          ) {
            return true;
          }
        }
      }
      return false;
    });

    if (!isBlocking) return yaml;

    const ref: string = activeDialogRef;
    const lineRe = new RegExp(
      `(- (?:dialog|alertdialog)\\b[^\\n]*?\\[ref=${ref}\\][^\\n]*?)(:?\\n|:?$)`,
      'm'
    );
    const augmented = yaml.replace(lineRe, (_match, head: string, tail: string) => {
      if (head.includes('[aria-modal=true]')) return head + tail;
      return `${head} [aria-modal=true]${tail}`;
    });

    logger.debug('[DirectPlaywright] Stamped blocking-modal signal', { ref });
    return augmented;
  } catch (err) {
    logger.debug('[DirectPlaywright] Blocking-modal augmentation failed', {
      error: errorMessage(err),
    });
    return yaml;
  }
}
